/**
 * Pure aggregation utilities for the API Dashboard.
 *
 * Backend hasn't shipped `/api_keys/usage/daily` yet, so we derive the
 * dashboard view contract from raw `/api_keys/logs` entries client-side.
 * When backend ships the aggregated endpoint, we replace the call site in
 * the Vuex action — these utilities remain useful for tests and as fallback.
 *
 * Log entry shape (from getApiKeyLogs response.query_logs):
 *   {
 *     query_log_id: string,
 *     create_at: string (ISO timestamp),
 *     email: string,
 *     log_type_name: string,             // e.g. "People Search"
 *     authentication_type_name: string,
 *     masked_authentication: string,     // e.g. "ak_••••abc123"
 *     request_body: object
 *   }
 *
 * Per-call credit cost is NOT available in the legacy logs endpoint.
 * KPIs and tables show CALLS only until backend provides credit data.
 */

import moment from "moment";

const DAY_KEY_FORMAT = "YYYY-MM-DD";
const DAY_KEY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Normalize Date | YYYY-MM-DD | ISO string → YYYY-MM-DD in local time. The
 * Element UI date-range picker emits Dates, preset clicks emit strings; the
 * API takes one shape (string) regardless of source.
 */
export function toDayKey(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" && DAY_KEY_REGEX.test(value)) return value;
  const m = moment(value);
  return m.isValid() ? m.format(DAY_KEY_FORMAT) : null;
}

export function rangeDays(from, to) {
  const start = moment(from).startOf("day");
  const end = moment(to).startOf("day");
  const days = [];
  for (let d = start.clone(); d.isSameOrBefore(end); d.add(1, "day")) {
    days.push(d.format(DAY_KEY_FORMAT));
  }
  return days;
}

export function isInRange(timestamp, from, to) {
  const t = moment(timestamp);
  if (!t.isValid()) return false;
  return t.isBetween(moment(from).startOf("day"), moment(to).endOf("day"), null, "[]");
}

export function filterLogs(logs, { from, to, keys } = {}) {
  if (!Array.isArray(logs)) return [];
  return logs.filter((log) => {
    if (from && to && !isInRange(log.create_at, from, to)) return false;
    if (keys && keys.length > 0 && !keys.includes(log.masked_authentication)) {
      return false;
    }
    return true;
  });
}

export function bucketByDay(logs, from, to) {
  const buckets = new Map();
  for (const day of rangeDays(from, to)) {
    buckets.set(day, 0);
  }
  for (const log of logs) {
    const key = toDayKey(log.create_at);
    if (key && buckets.has(key)) {
      buckets.set(key, buckets.get(key) + 1);
    }
  }
  return Array.from(buckets.entries()).map(([date, calls]) => ({ date, calls }));
}

export function groupBy(logs, fieldName) {
  const counts = new Map();
  for (const log of logs) {
    const value = log[fieldName] || "(unknown)";
    counts.set(value, (counts.get(value) || 0) + 1);
  }
  const total = logs.length;
  return Array.from(counts.entries())
    .map(([name, calls]) => ({
      name,
      calls,
      percent: total > 0 ? (calls / total) * 100 : 0,
    }))
    .sort((a, b) => b.calls - a.calls);
}

export function computeKpis(logs, from, to) {
  if (!Array.isArray(logs) || logs.length === 0) {
    return {
      totalCalls: 0,
      avgPerDay: 0,
      topEndpoint: null,
      topKey: null,
    };
  }

  const days = rangeDays(from, to);
  const totalCalls = logs.length;
  const avgPerDay = days.length > 0 ? totalCalls / days.length : 0;

  const byEndpoint = groupBy(logs, "log_type_name");
  const byKey = groupBy(logs, "masked_authentication");

  return {
    totalCalls,
    avgPerDay,
    topEndpoint: byEndpoint[0] || null,
    topKey: byKey[0] || null,
  };
}

export function uniqueKeys(logs) {
  const set = new Set();
  for (const log of logs) {
    if (log.masked_authentication) set.add(log.masked_authentication);
  }
  return Array.from(set).sort();
}
